import { useEffect, useState } from 'react';
import {
  apiErrorText,
  type ChronumPayrollCategory,
  type ChronumPosition,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import { IconCheck } from '@shared/ui/icons';
import { updatedLine } from '@entities/chronum';
import { isStaleError } from '../apiErrors';
import {
  CommentField,
  CommentView,
  DetailsGrid,
  DetailsTitle,
  MappingJournal,
  SaveErrorNotice,
  type SaveError,
} from '../MappingParts';
import type { PositionSaver } from '../usePositionSaver';
import { FormActions, Updated } from './styles';

export function uniqueCompanies(p: ChronumPosition): string[] {
  return [...new Set(p.companies.map((c) => c.trim()).filter(Boolean))];
}

/** Комментарий и журнал правок должности — под её строкой. */
export function PositionDetails({
  position,
  categories,
  canWrite,
  saver,
  listUpdatedAt,
}: {
  position: ChronumPosition;
  categories: readonly ChronumPayrollCategory[];
  canWrite: boolean;
  saver: PositionSaver;
  /** Когда пришёл последний ответ списка должностей. */
  listUpdatedAt: number;
}) {
  const saved = position.comment ?? '';
  const [draft, setDraft] = useState(saved);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<SaveError | null>(null);
  const [done, setDone] = useState(false);
  /** «Перечитать»: с какого момента ждём свежий список. */
  const [rereadSince, setRereadSince] = useState<number | null>(null);
  const unchanged = draft.trim() === saved.trim();
  const updated = updatedLine(position.updated_at, position.updated_by_name);

  // «Перечитать» закончилось, когда пришёл список, запрошенный после нажатия:
  // плашка уходит вместе с появлением свежих данных, а не раньше.
  useEffect(() => {
    if (rereadSince === null || listUpdatedAt <= rereadSince) return;
    setRereadSince(null);
    setError(null);
  }, [rereadSince, listUpdatedAt]);

  async function save() {
    setBusy(true);
    setError(null);
    setDone(false);
    try {
      await saver.saveComment(position, draft.trim());
      setDraft(draft.trim());
      setDone(true);
    } catch (err) {
      setError({
        message: apiErrorText(err, 'Комментарий не сохранился.'),
        stale: isStaleError(err),
      });
    } finally {
      setBusy(false);
    }
  }

  function reread() {
    setRereadSince(Date.now());
    saver.reread(position.key);
  }

  return (
    <DetailsGrid>
      <section aria-label={`Комментарий к должности «${position.name}»`}>
        <DetailsTitle>Комментарий</DetailsTitle>
        {canWrite ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!unchanged && !busy) void save();
            }}
          >
            <CommentField
              label={`Комментарий к должности «${position.name}»`}
              value={draft}
              onChange={(v) => {
                setDraft(v);
                setDone(false);
              }}
              disabled={busy}
            />
            <FormActions>
              <Button type="submit" $variant="primary" $size="sm" disabled={busy || unchanged}>
                {busy ? 'Сохраняю…' : 'Сохранить комментарий'}
              </Button>
              {!unchanged && !busy && (
                <Button type="button" $variant="ghost" $size="sm" onClick={() => setDraft(saved)}>
                  Вернуть сохранённый
                </Button>
              )}
              {done && unchanged && (
                <span className="done" role="status">
                  <IconCheck size={14} />
                  Сохранено
                </span>
              )}
            </FormActions>
            {error && (
              <SaveErrorNotice
                error={error}
                busy={rereadSince !== null}
                onReread={reread}
                onRetry={() => void save()}
              />
            )}
          </form>
        ) : (
          <CommentView value={position.comment} />
        )}
        {updated && <Updated>{updated}</Updated>}
      </section>
      <section aria-label={`Журнал правок должности «${position.name}»`}>
        <DetailsTitle>Журнал правок</DetailsTitle>
        <MappingJournal entity="position" entityKey={position.key} categories={categories} />
      </section>
    </DetailsGrid>
  );
}
